import { ActionIcon, Group, Tooltip } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';

import classes from './ReviewVerdictControls.module.css';

/** One verdict button: the decision it records, its accessible name (also
 * the tooltip) and its test id. */
export interface ReviewVerdict<TDecision extends string> {
  value: TDecision;
  label: string;
  testId: string;
}

export interface ReviewVerdictControlsProps<TDecision extends string> {
  /** The red cross, on the left. */
  reject: ReviewVerdict<TDecision>;
  /** The green tick, on the right. */
  confirm: ReviewVerdict<TDecision>;
  /** The verdict on record is shown filled; `null` leaves both light. */
  decision: TDecision | null;
  disabled?: boolean;
  onDecide: (decision: TDecision) => void | Promise<void>;
}

/**
 * The reject / confirm pair of a review card (match review, profile
 * review). It sits in the card's header so a card can be decided without
 * scrolling past its detail; the card owns the note that goes with the
 * verdict and the decision colour of its border.
 */
export function ReviewVerdictControls<TDecision extends string>({
  reject,
  confirm,
  decision,
  disabled = false,
  onDecide,
}: Readonly<ReviewVerdictControlsProps<TDecision>>) {
  const buttons = [
    { verdict: reject, color: 'red', Icon: IconX },
    { verdict: confirm, color: 'green', Icon: IconCheck },
  ];

  return (
    <Group gap={4} wrap="nowrap" className={classes.verdict}>
      {buttons.map(({ verdict, color, Icon }) => (
        <Tooltip key={verdict.value} label={verdict.label} transitionProps={{ duration: 0 }}>
          <ActionIcon
            variant={decision === verdict.value ? 'filled' : 'light'}
            color={color}
            size="md"
            aria-label={verdict.label}
            disabled={disabled}
            onClick={() => onDecide(verdict.value)}
            data-testid={verdict.testId}
          >
            <Icon size={16} />
          </ActionIcon>
        </Tooltip>
      ))}
    </Group>
  );
}
